/**
 * UserStats 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'

/**
 * UserStats
 */
export interface UserStats {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键，和主表的主键一致 */
  id?: string
  /** 关注数 */
  following_count?: number
  /** 粉丝数 */
  follower_count?: number
  /** 好友数 */
  friend_count?: number
  /** 发布的动态数 */
  post_count?: number
  /** 获得的点赞总数 */
  received_like_count?: number
  /** 获得的评论总数 */
  received_comment_count?: number
  /** 加入的群组数 */
  joined_group_count?: number
  /** 加入的大本营群组数 */
  joined_camp_count?: number
  /** 加入的对线群组数 */
  joined_battle_count?: number
  /** 创建的群组数 */
  created_group_count?: number
  /** 活跃天数 */
  active_day_count?: number
  /** 最后活跃时间（UTC） */
  last_active_at?: string
  /** 连续活跃天数 */
  continuous_active_days?: number
  /** 发送的消息总数 */
  sent_message_count?: number
  /** 举报次数 */
  report_count?: number
  /** 被举报次数 */
  reported_count?: number
  /** 对线总次数 */
  battle_total_count?: number
  /** 对线胜利次数 */
  battle_win_count?: number
  /** 对线失败次数 */
  battle_lose_count?: number
  /** 对线平局次数 */
  battle_draw_count?: number
  /** 对线胜率 */
  battle_win_rate?: number
  /** 对线失败率 */
  battle_lose_rate?: number
  /** 对线MVP次数 */
  battle_mvp_count?: number
  /** 访客总数（去重） */
  visitor_count?: number
}

/**
 * UserStats Schema
 */
export const UserStatsSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键，和主表的主键一致',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    following_count: {
      description: '关注数',
      format: 'int',
      type: 'integer',
      label: '关注数',
    },
    follower_count: {
      description: '粉丝数',
      format: 'int',
      type: 'integer',
      label: '粉丝数',
    },
    friend_count: {
      description: '好友数',
      format: 'int',
      type: 'integer',
      label: '好友数',
    },
    post_count: {
      description: '发布的动态数',
      format: 'int',
      type: 'integer',
      label: '发布的动态数',
    },
    received_like_count: {
      description: '获得的点赞总数',
      format: 'int64',
      type: 'integer',
      label: '获得的点赞总数',
    },
    received_comment_count: {
      description: '获得的评论总数',
      format: 'int64',
      type: 'integer',
      label: '获得的评论总数',
    },
    joined_group_count: {
      description: '加入的群组数',
      format: 'int',
      type: 'integer',
      label: '加入的群组数',
    },
    joined_camp_count: {
      description: '加入的大本营群组数',
      format: 'int',
      type: 'integer',
      label: '加入的大本营群组数',
    },
    joined_battle_count: {
      description: '加入的对线群组数',
      format: 'int',
      type: 'integer',
      label: '加入的对线群组数',
    },
    created_group_count: {
      description: '创建的群组数',
      format: 'int',
      type: 'integer',
      label: '创建的群组数',
    },
    active_day_count: {
      description: '活跃天数',
      format: 'int',
      type: 'integer',
      label: '活跃天数',
    },
    last_active_at: {
      description: '最后活跃时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '最后活跃时间',
    },
    continuous_active_days: {
      description: '连续活跃天数',
      format: 'int',
      type: 'integer',
      label: '连续活跃天数',
    },
    sent_message_count: {
      description: '发送的消息总数',
      format: 'int64',
      type: 'integer',
      label: '发送的消息总数',
    },
    report_count: {
      description: '举报次数',
      format: 'int',
      type: 'integer',
      label: '举报次数',
    },
    reported_count: {
      description: '被举报次数',
      format: 'int',
      type: 'integer',
      label: '被举报次数',
    },
    battle_total_count: {
      description: '对线总次数',
      format: 'int',
      type: 'integer',
      label: '对线总次数',
    },
    battle_win_count: {
      description: '对线胜利次数',
      format: 'int',
      type: 'integer',
      label: '对线胜利次数',
    },
    battle_lose_count: {
      description: '对线失败次数',
      format: 'int',
      type: 'integer',
      label: '对线失败次数',
    },
    battle_draw_count: {
      description: '对线平局次数',
      format: 'int',
      type: 'integer',
      label: '对线平局次数',
    },
    battle_win_rate: {
      description: '对线胜率',
      format: 'float64',
      type: 'number',
      label: '对线胜率',
    },
    battle_lose_rate: {
      description: '对线失败率',
      format: 'float64',
      type: 'number',
      label: '对线失败率',
    },
    battle_mvp_count: {
      description: '对线MVP次数',
      format: 'int',
      type: 'integer',
      label: '对线MVP次数',
    },
    visitor_count: {
      description: '访客总数（去重）',
      format: 'int',
      type: 'integer',
      label: '访客总数',
    },
  },
  type: 'object',
}
